import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { useSearchParams } from "react-router-dom";
import {
  MessageSquare,
  Sparkles,
  Search,
  ArrowLeft,
  ArrowUpRight,
  Clock,
  Check,
  CheckCircle2,
  XCircle,
  User,
  Loader2,
  Inbox,
  Send,
  RefreshCw,
  Zap,
  Crown,
  SlidersHorizontal,
  ChevronRight,
  Activity,
  Network,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Card } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { readEdgeError } from "@/lib/edgeError";
import { useReplyWindow } from "@/hooks/useReplyWindow";
import { useContactAvatarUrls } from "@/hooks/useContactAvatars";
import { ReplyWindowNotice, DeliveryFailedNote } from "./ReplyWindowNotice";
import { ReplyTemplatePicker } from "./ReplyTemplatePicker";
import { formatDistanceToNow, format, isToday, isYesterday } from "date-fns";
import { useMacQuota } from "@/hooks/useMacQuota";
import { useMacOveragePurchase } from "@/hooks/useMacOveragePurchase";
import { plans } from "@/config/stripePlans";
import {
  WhatsAppIcon,
  TelegramIcon,
  InstagramIcon,
  MessengerIcon,
  LineIcon,
  TikTokIcon,
} from "./BrandIcons";
import AdminTabActionBar from "./AdminTabActionBar";
import { createInboxSamples } from "@/lib/inboxSamples";
import {
  classifyPipelineStage,
  STAGE_META,
  type PipelineStage,
} from "@/lib/pipelineStage";
import { associateUnifiedContacts } from "@/lib/unifiedContacts";
import {
  channelTypeForChannel,
  conversationIdentity,
  formatPhoneForDisplay,
  nameWithIdentity,
} from "@/lib/contactDisplay";

// Types

interface Conversation {
  id: string;
  customer_id?: string | null;
  customer_name?: string | null;
  /** @username of this chat's sender (Instagram/Telegram), when known. */
  customer_handle?: string | null;
  customer_email?: string | null;
  customer_contact_phone?: string | null;
  customer_contact_id?: string | null;
  customer_status?: PipelineStage | null;
  customer_phone: string;
  status: "active" | "human_takeover" | "closed";
  created_at: string;
  updated_at: string;
  channel?: string | null;
  channel_user_id?: string | null;
  last_message?: string;
  last_message_time?: string;
  message_count?: number;
  is_sample?: boolean;
}

interface Message {
  id: string;
  conversation_id: string;
  sender_type: "customer" | "ai" | "human_owner";
  content: string;
  created_at: string;
  delivery_failed_at?: string | null;
  delivery_error?: unknown;
}

interface CustomerContextPanelData {
  synopsis: string | null;
  recommendedNextStep: string | null;
  attributes: Array<{ label: string; value: string }>;
}

// Helpers

const STATUS_CONFIG = {
  active: {
    label: "AI active",
    color: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800",
    icon: CheckCircle2,
  },
  human_takeover: {
    label: "Human handling",
    color: "bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800",
    icon: User,
  },
  closed: {
    label: "Closed",
    color: "bg-zinc-500/10 text-zinc-500 dark:text-zinc-400 border-zinc-200 dark:border-zinc-700",
    icon: XCircle,
  },
};

function FilterCheckbox({ checked }: { checked: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        "ml-auto flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors",
        checked ? "border-foreground bg-foreground text-background" : "border-input bg-card",
      )}
    >
      {checked && <Check className="h-3 w-3" strokeWidth={3} />}
    </span>
  );
}

type PlatformFilter = "whatsapp" | "telegram" | "instagram" | "facebook_messenger" | "line" | "tiktok";
type AttentionFilter = "human" | "ai";
type ConversationFilterPage = "menu" | "platform" | "status" | "attention";

const PLATFORM_FILTER_OPTIONS: ReadonlyArray<{ value: PlatformFilter; label: string }> = [
  { value: "whatsapp", label: "WhatsApp" },
  { value: "telegram", label: "Telegram" },
  { value: "instagram", label: "Instagram" },
  { value: "facebook_messenger", label: "Messenger" },
  { value: "line", label: "LINE" },
  { value: "tiktok", label: "TikTok" },
];

const CUSTOMER_STATUS_FILTER_OPTIONS: ReadonlyArray<{ value: PipelineStage; label: string }> = [
  { value: "new", label: "New" },
  { value: "active", label: "Active" },
  { value: "at-risk", label: "At risk" },
  { value: "dormant", label: "Dormant" },
  { value: "lapsed", label: "Lapsed" },
  { value: "vip", label: "VIP" },
];

const ATTENTION_FILTER_OPTIONS: ReadonlyArray<{ value: AttentionFilter; label: string }> = [
  { value: "human", label: "Human" },
  { value: "ai", label: "AI" },
];

function formatPhone(phone: string) {
  // Clean up whatsapp: prefix
  const clean = phone.replace("whatsapp:", "").trim();
  return clean;
}

// Channel-aware identity for a conversation. Telegram conversations carry
// a numeric chat_id in `customer_phone` (legacy field name). Render it as
// "Telegram · <id>" rather than parading a fake phone number.
function formatConversationIdentity(convo: Pick<Conversation, "channel" | "customer_phone" | "channel_user_id">) {
  const ch = (convo.channel || "whatsapp").toLowerCase();
  if (ch === "telegram") {
    const id = convo.channel_user_id || convo.customer_phone || "";
    return `Telegram · ${id}`;
  }
  if (ch === "instagram") {
    const id = convo.channel_user_id || convo.customer_phone || "";
    return `Instagram · ${id}`;
  }
  if (ch === "facebook_messenger") {
    const id = convo.channel_user_id || convo.customer_phone || "";
    return `Messenger · ${id}`;
  }
  if (ch === "line") {
    const id = convo.channel_user_id || convo.customer_phone || "";
    return `LINE · ${id}`;
  }
  if (ch === "tiktok") {
    const id = convo.channel_user_id || convo.customer_phone || "";
    return `TikTok · ${id}`;
  }
  return formatPhone(convo.customer_phone);
}

// The chat's own identity: the WhatsApp number, the Instagram/Telegram
// @username, or the email it is on. See src/lib/contactDisplay.ts.
function conversationIdentityLabel(convo: Conversation) {
  return conversationIdentity({
    channel: convo.channel,
    channelUserId: convo.channel_user_id || convo.customer_phone,
    handle: convo.customer_handle,
  }) || (convo.customer_contact_phone ? formatPhoneForDisplay(convo.customer_contact_phone) : null);
}

function conversationDisplayName(convo: Conversation) {
  return nameWithIdentity(convo.customer_name, conversationIdentityLabel(convo));
}

function conversationInitials(convo: Conversation) {
  const label = conversationDisplayName(convo);
  const initials = label
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.replace(/^[@+]/, "").charAt(0).toUpperCase())
    .join("");
  return initials || "?";
}

function normalizeCustomerStatus(value: unknown): PipelineStage | null {
  const status = String(value ?? "").toLowerCase();
  if (status === "at_risk") return "at-risk";
  if (["new", "active", "at-risk", "dormant", "lapsed", "vip"].includes(status)) {
    return status as PipelineStage;
  }
  return null;
}

function formatCustomerAttributeLabel(key: string) {
  return key
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

function formatCustomerAttributeValue(value: unknown): string | null {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "string" || typeof value === "number") return String(value);
  if (Array.isArray(value)) {
    const items = value.filter((item) => typeof item === "string" || typeof item === "number");
    return items.length > 0 ? items.join(", ") : null;
  }
  return null;
}

const IDENTITY_LOOKUP_CHUNK = 100;

/**
 * Name and @username for each conversation from the Customer Graph: most
 * messaging-only chats have no CRM customer record, so without this they had
 * no name at all. Keyed by conversation id; best-effort (empty on error).
 */
async function loadConversationIdentities(businessId: string, convos: any[]) {
  const result = new Map<string, { name: string | null; handle: string | null; contactId: string }>();
  const keyFor = (channelType: string, value: string) => `${channelType}|${value}`;
  const wanted = new Map<string, string[]>(); // identifier key → conversation ids
  for (const convo of convos) {
    const channelType = channelTypeForChannel(convo.channel ?? "whatsapp");
    const raw = String(convo.channel_user_id || convo.customer_phone || "").replace(/^whatsapp:/i, "").trim();
    if (!channelType || !raw) continue;
    const value = channelType === "whatsapp_e164" ? `+${raw.replace(/\D/g, "")}` : raw;
    const key = keyFor(channelType, value);
    wanted.set(key, [...(wanted.get(key) ?? []), convo.id]);
  }
  if (wanted.size === 0) return result;

  try {
    const values = [...new Set([...wanted.keys()].map((key) => key.split("|")[1]))];
    const identifiers: Array<{ contact_id: string; channel_type: string; identifier_value: string; handle: string | null }> = [];
    for (let index = 0; index < values.length; index += IDENTITY_LOOKUP_CHUNK) {
      const { data, error } = await (supabase as any)
        .from("channel_identifiers")
        .select("contact_id, channel_type, identifier_value, handle")
        .eq("business_id", businessId)
        .in("identifier_value", values.slice(index, index + IDENTITY_LOOKUP_CHUNK));
      if (error) throw error;
      identifiers.push(...(data ?? []));
    }

    const contactIds = [...new Set(identifiers.map((identifier) => identifier.contact_id))];
    const contacts = new Map<string, { display_name: string | null; merged_into: string | null }>();
    // Follow merges (at most a few hops) so a merged chat shows the survivor.
    let pending = contactIds;
    for (let hop = 0; hop < 4 && pending.length > 0; hop += 1) {
      const next: string[] = [];
      for (let index = 0; index < pending.length; index += IDENTITY_LOOKUP_CHUNK) {
        const { data, error } = await (supabase as any)
          .from("contacts")
          .select("contact_id, display_name, merged_into")
          .eq("business_id", businessId)
          .in("contact_id", pending.slice(index, index + IDENTITY_LOOKUP_CHUNK));
        if (error) throw error;
        for (const contact of data ?? []) {
          contacts.set(contact.contact_id, contact);
          if (contact.merged_into && !contacts.has(contact.merged_into)) next.push(contact.merged_into);
        }
      }
      pending = next;
    }
    const live = (contactId: string) => {
      let current = contactId;
      for (let hop = 0; hop < 4; hop += 1) {
        const merged = contacts.get(current)?.merged_into;
        if (!merged) break;
        current = merged;
      }
      return current;
    };

    for (const identifier of identifiers) {
      const conversationIds = wanted.get(keyFor(identifier.channel_type, identifier.identifier_value));
      if (!conversationIds) continue;
      const contactId = live(identifier.contact_id);
      const name = contacts.get(contactId)?.display_name?.trim() || null;
      for (const conversationId of conversationIds) {
        result.set(conversationId, { name, handle: identifier.handle ?? null, contactId });
      }
    }
  } catch (error) {
    console.warn("Conversation identities unavailable:", error);
  }
  return result;
}

async function loadCustomerStatuses(businessId: string, convos: any[]) {
  const customerById = new Map<string, {
    customer_id: string;
    email: string | null;
    phone: string | null;
    created_at: string;
    last_booking_at: string | null;
  }>();

  for (const convo of convos) {
    const customer = convo.customers;
    if (!convo.customer_id || !customer) continue;
    customerById.set(convo.customer_id, {
      customer_id: convo.customer_id,
      email: customer.email ?? null,
      phone: customer.phone ?? null,
      created_at: customer.created_at ?? convo.created_at,
      last_booking_at: customer.last_booking_at ?? null,
    });
  }

  if (customerById.size === 0) {
    return new Map<string, { status: PipelineStage; contactId: string | null }>();
  }

  const [{ data: contacts }, { data: projections }, { data: identifiers }] = await Promise.all([
    supabase
      .from("contacts")
      .select("contact_id, primary_email, primary_phone")
      .eq("business_id", businessId)
      .is("tombstoned_at", null),
    supabase
      .from("contact_projections")
      .select("contact_id, status, last_active_at, total_messages, total_bookings")
      .eq("business_id", businessId),
    supabase
      .from("channel_identifiers")
      .select("contact_id, identifier_value")
      .eq("business_id", businessId),
  ]);

  const associations = associateUnifiedContacts({
    customers: [...customerById.values()],
    contacts: contacts ?? [],
    projections: projections ?? [],
    channelIdentifiers: identifiers ?? [],
  });
  const statuses = new Map<string, { status: PipelineStage; contactId: string | null }>();

  for (const association of associations) {
    if (!association.customer) continue;
    const customer = customerById.get(association.customer.customer_id);
    if (!customer) continue;

    const projection = association.projection as (typeof association.projection & {
      status?: string | null;
      last_active_at?: string | null;
    });
    const projectedStatus = normalizeCustomerStatus(projection?.status);
    statuses.set(customer.customer_id, {
      status: projectedStatus ?? classifyPipelineStage({
        recent_order_value: 0,
        total_order_value: customer.last_booking_at ? 1 : 0,
        created_at: customer.created_at,
        last_activity_at: projection?.last_active_at ?? customer.last_booking_at,
      }, Infinity),
      contactId: association.contact?.contact_id ?? null,
    });
  }

  return statuses;
}

async function enrichConversations(
  convos: any[],
  customerStatuses = new Map<string, { status: PipelineStage; contactId: string | null }>(),
  identities = new Map<string, { name: string | null; handle: string | null; contactId: string }>(),
): Promise<Conversation[]> {
  return Promise.all(
    convos.map(async (convo: any) => {
      const { data: lastMsg } = await supabase
        .from("ai_messages")
        .select("content, created_at")
        .eq("conversation_id", convo.id)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      const { count } = await supabase
        .from("ai_messages")
        .select("id", { count: "exact", head: true })
        .eq("conversation_id", convo.id);

      const customerContext = customerStatuses.get(convo.customer_id);
      const identity = identities.get(convo.id);
      return {
        ...convo,
        customer_name: convo.customers?.name ?? identity?.name ?? null,
        customer_handle: identity?.handle ?? null,
        customer_email: convo.customers?.email ?? null,
        customer_contact_phone: convo.customers?.phone ?? null,
        customer_contact_id: convo.customer_contact_id ?? customerContext?.contactId ?? identity?.contactId ?? null,
        customer_status: convo.customer_status ?? customerContext?.status ?? "new",
        last_message: lastMsg?.content || "",
        last_message_time: lastMsg?.created_at || convo.updated_at,
        message_count: count || 0,
      } as Conversation;
    }),
  );
}

function channelBadge(channel: string | null | undefined) {
  const ch = (channel || "whatsapp").toLowerCase();
  if (ch === "telegram") {
    return {
      Icon: TelegramIcon,
      bg: "from-[#229ED9]/20 to-[#229ED9]/5",
      iconClass: "text-[#229ED9]",
      label: "Telegram",
    };
  }
  if (ch === "instagram") {
    return {
      Icon: InstagramIcon,
      bg: "from-[#E4405F]/20 to-[#E4405F]/5",
      iconClass: "text-[#E4405F]",
      label: "Instagram",
    };
  }
  if (ch === "facebook_messenger") {
    return {
      Icon: MessengerIcon,
      bg: "from-[#0084FF]/20 to-[#0084FF]/5",
      iconClass: "text-[#0084FF]",
      label: "Messenger",
    };
  }
  if (ch === "line") {
    return {
      Icon: LineIcon,
      bg: "from-[#06C755]/20 to-[#06C755]/5",
      iconClass: "text-[#06C755]",
      label: "LINE",
    };
  }
  if (ch === "tiktok") {
    return {
      Icon: TikTokIcon,
      bg: "from-neutral-500/20 to-neutral-500/5",
      iconClass: "text-foreground",
      label: "TikTok",
    };
  }
  return {
    Icon: WhatsAppIcon,
    bg: "from-[#25D366]/20 to-[#25D366]/5",
    iconClass: "text-[#25D366]",
    label: "WhatsApp",
  };
}

function formatMessageDate(dateStr: string) {
  const date = new Date(dateStr);
  if (isToday(date)) return format(date, "h:mm a");
  if (isYesterday(date)) return "Yesterday";
  return format(date, "MMM d");
}

function conversationBelongsToCustomer(
  conversation: Conversation,
  customerId: string | null,
  contactId: string | null,
) {
  if (!customerId && !contactId) return true;
  return Boolean(
    (customerId && conversation.customer_id === customerId)
    || (contactId && conversation.customer_contact_id === contactId),
  );
}

// Render the lightweight formatting supported by WhatsApp while keeping all
// message text escaped. Double asterisks are also accepted because some AI
// responses arrive with Markdown-style emphasis.
function MessageContent({ content }: { content: string }) {
  const formattingPattern = /(```[\s\S]*?```|\*\*[^*\n]+?\*\*|\*(?![\s*])[^*\n]+?(?<!\s)\*|_[^_\n]+?_|~[^~\n]+?~)/g;
  const parts = content.split(formattingPattern);

  return (
    <p className="whitespace-pre-wrap [overflow-wrap:anywhere]">
      {parts.map((part, index) => {
        if (part.startsWith("```") && part.endsWith("```")) {
          return (
            <code key={index} className="rounded bg-foreground/5 px-1 py-0.5 font-mono text-[0.92em]">
              {part.slice(3, -3)}
            </code>
          );
        }
        if (part.startsWith("**") && part.endsWith("**")) {
          return <strong key={index}>{part.slice(2, -2)}</strong>;
        }
        if (part.startsWith("*") && part.endsWith("*")) {
          return <strong key={index}>{part.slice(1, -1)}</strong>;
        }
        if (part.startsWith("_") && part.endsWith("_")) {
          return <em key={index}>{part.slice(1, -1)}</em>;
        }
        if (part.startsWith("~") && part.endsWith("~")) {
          return <s key={index}>{part.slice(1, -1)}</s>;
        }
        return part;
      })}
    </p>
  );
}

function groupMessagesByDate(messages: Message[]) {
  const groups: { label: string; messages: Message[] }[] = [];
  let currentLabel = "";

  for (const msg of messages) {
    const date = new Date(msg.created_at);
    let label: string;
    if (isToday(date)) label = "Today";
    else if (isYesterday(date)) label = "Yesterday";
    else label = format(date, "EEEE, MMM d, yyyy");

    if (label !== currentLabel) {
      currentLabel = label;
      groups.push({ label, messages: [msg] });
    } else {
      groups[groups.length - 1].messages.push(msg);
    }
  }
  return groups;
}

// Main Component

const Conversations = () => {
  const { businessId, subscriptionTier } = useAuth();
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const scopedCustomerId = searchParams.get("customer");
  const scopedContactId = searchParams.get("contact");
  const hasCustomerScope = Boolean(scopedCustomerId || scopedContactId);

  // State
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoadingConvos, setIsLoadingConvos] = useState(true);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [mobileShowChat, setMobileShowChat] = useState(false);
  const [messageInput, setMessageInput] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showingSampleInbox, setShowingSampleInbox] = useState(false);
  const [customerContext, setCustomerContext] = useState<CustomerContextPanelData | null>(null);
  const [isLoadingCustomerContext, setIsLoadingCustomerContext] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterPage, setFilterPage] = useState<ConversationFilterPage>("menu");
  const [platformFilters, setPlatformFilters] = useState<PlatformFilter[]>(
    PLATFORM_FILTER_OPTIONS.map(({ value }) => value),
  );
  const [customerStatusFilters, setCustomerStatusFilters] = useState<PipelineStage[]>(
    CUSTOMER_STATUS_FILTER_OPTIONS.map(({ value }) => value),
  );
  const [attentionFilters, setAttentionFilters] = useState<AttentionFilter[]>(
    ATTENTION_FILTER_OPTIONS.map(({ value }) => value),
  );
  const sampleInbox = useMemo(() => createInboxSamples(), []);

  // MAC quota state (replaces legacy ai_message_usage read)
  const [quotaModalOpen, setQuotaModalOpen] = useState(false);
  const quotaModalShownOnLoadRef = useRef(false);
  const macQuota = useMacQuota(businessId);
  const { purchase: purchaseOverage, isPurchasing: overageLoading } = useMacOveragePurchase(macQuota.refetch);
  const isUnlimitedPlan = macQuota.isUnlimited || subscriptionTier?.toLowerCase() === "enterprise";
  const isQuotaExhausted = macQuota.isExhausted;
  const quotaResetDate = macQuota.cycleEnd
    ? macQuota.cycleEnd.toLocaleDateString("en-SG", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "Asia/Singapore",
    })
    : "";

  const messagesEndRef = useRef<HTMLDivElement>(null);
  // Tracks which conversation we last scrolled for, so opening a new
  // conversation jumps instantly to the latest message (no slow auto-scroll
  // from the top. Ugly on long threads, especially on mobile) while new
  // incoming messages in the *same* conversation still animate smoothly.
  const lastScrolledConvRef = useRef<string | null>(null);

  // WhatsApp / Instagram / Messenger only deliver replies within 24h of the
  // customer's last message. Block the composer (with the reason) after that.
  // Only this conversation's messages count: `messages` can briefly still hold
  // the previous thread while a new one loads.
  const seenCustomerAt = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (m.conversation_id === selectedConversation?.id && m.sender_type === "customer") return m.created_at;
    }
    return null;
  }, [messages, selectedConversation?.id]);
  const replyWindow = useReplyWindow(businessId, selectedConversation, seenCustomerAt);
  const replyBlocked =
    selectedConversation?.status === "human_takeover" && replyWindow.applies && replyWindow.open === false;

  // Scroll to bottom when messages change
  useEffect(() => {
    if (isLoadingMessages) return;
    const convId = selectedConversation?.id ?? null;
    const switchedConversation = lastScrolledConvRef.current !== convId;
    messagesEndRef.current?.scrollIntoView({
      behavior: switchedConversation ? "auto" : "smooth",
    });
    lastScrolledConvRef.current = convId;
  }, [isLoadingMessages, messages, selectedConversation?.id]);

  // Show modal once on first load if MAC quota is exhausted.
  useEffect(() => {
    if (macQuota.isLoading || isUnlimitedPlan) return;
    if (macQuota.isExhausted && !quotaModalShownOnLoadRef.current) {
      quotaModalShownOnLoadRef.current = true;
      setQuotaModalOpen(true);
    }
  }, [macQuota.isLoading, macQuota.isExhausted, isUnlimitedPlan]);

  // Fetch conversations
  const fetchConversations = useCallback(async () => {
    if (!businessId) return;
    setIsLoadingConvos(true);
    try {
      const { data: convos, error } = await supabase
        .from("ai_conversations")
        .select("*, customers(name, email, phone, created_at, last_booking_at)")
        .eq("business_id", businessId)
        .order("updated_at", { ascending: false });

      if (error) throw error;

      const [customerStatuses, identities] = await Promise.all([
        loadCustomerStatuses(businessId, convos || []),
        loadConversationIdentities(businessId, convos || []),
      ]);
      const enriched = await enrichConversations(convos || [], customerStatuses, identities);
      if (enriched.length === 0) {
        const { data: sampleState } = await supabase
          .from("customer_sample_states")
          .select("retired_at")
          .eq("business_id", businessId)
          .maybeSingle();

        if (sampleState?.retired_at === null) {
          setConversations(sampleInbox.conversations);
          setShowingSampleInbox(true);
          return;
        }
      }

      setConversations(enriched);
      setShowingSampleInbox(false);
    } catch (e: any) {
      console.error("Error fetching conversations:", e);
      toast({ title: "Error", description: "Failed to load conversations.", variant: "destructive" });
    } finally {
      setIsLoadingConvos(false);
    }
  }, [businessId, sampleInbox.conversations, toast]);

  useEffect(() => {
    fetchConversations();

    if (!businessId) return;

    // Global listener for conversations and messages (for the sidebar)
    const channel = supabase
      .channel("global-conversations")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "ai_conversations",
          filter: `business_id=eq.${businessId}`,
        },
        (payload) => {
          if (payload.eventType === "INSERT") {
            const newConvo = payload.new as Conversation;
            setShowingSampleInbox(false);
            setSelectedConversation((prev) => prev?.is_sample ? null : prev);
            setMessages((prev) => prev.some((message) => message.conversation_id.startsWith("sample-inbox-")) ? [] : prev);
            setConversations((prev) => {
              if (prev.some((c) => c.id === newConvo.id)) return prev;
              return [
                {
                  ...newConvo,
                  last_message: "",
                  last_message_time: newConvo.updated_at,
                  message_count: 0,
                },
                ...prev.filter((conversation) => !conversation.is_sample),
              ];
            });
          } else if (payload.eventType === "UPDATE") {
            const updatedConvo = payload.new as Conversation;
            setConversations((prev) =>
              prev.map((c) =>
                c.id === updatedConvo.id ? { ...c, status: updatedConvo.status, updated_at: updatedConvo.updated_at } : c
              )
            );

            // Also update selectedConversation if it's the one that changed
            setSelectedConversation((prev) =>
              prev?.id === updatedConvo.id ? { ...prev, status: updatedConvo.status } : prev
            );
          }
        }
      )
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "ai_messages",
          filter: `business_id=eq.${businessId}`,
        },
        (payload) => {
          const newMsg = payload.new as Message;
          setConversations((prev) => {
            const convoIndex = prev.findIndex((c) => c.id === newMsg.conversation_id);
            if (convoIndex === -1) return prev; // If conversation isn't loaded yet, it will be by the other listener

            const updatedConvos = [...prev];
            const convo = updatedConvos[convoIndex];

            // Update the conversation's last message, timestamp, and count
            updatedConvos[convoIndex] = {
              ...convo,
              last_message: newMsg.content,
              last_message_time: newMsg.created_at,
              message_count: (convo.message_count || 0) + 1,
            };

            // Move this conversation to the top
            const [movedConvo] = updatedConvos.splice(convoIndex, 1);
            updatedConvos.unshift(movedConvo);

            return updatedConvos;
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [fetchConversations, businessId]);

  // Fetch messages for selected conversation
  const openConversation = useCallback(async (convo: Conversation) => {
    setSelectedConversation(convo);
    setMobileShowChat(true);
    if (convo.is_sample) {
      setMessages(sampleInbox.messagesByConversation[convo.id] || []);
      setIsLoadingMessages(false);
      return;
    }
    setIsLoadingMessages(true);
    try {
      const { data, error } = await supabase
        .from("ai_messages")
        .select("*")
        .eq("conversation_id", convo.id)
        .order("created_at", { ascending: true });

      if (error) throw error;
      setMessages((data || []) as Message[]);
    } catch (e: any) {
      console.error("Error fetching messages:", e);
      toast({ title: "Error", description: "Failed to load messages.", variant: "destructive" });
    } finally {
      setIsLoadingMessages(false);
    }
  }, [sampleInbox.messagesByConversation, toast]);

  useEffect(() => {
    if (isLoadingConvos || !hasCustomerScope) return;
    if (
      selectedConversation
      && conversationBelongsToCustomer(selectedConversation, scopedCustomerId, scopedContactId)
    ) {
      return;
    }

    const mostRecentConversation = conversations
      .filter((conversation) => conversationBelongsToCustomer(conversation, scopedCustomerId, scopedContactId))
      .sort((a, b) => {
        const aTime = new Date(a.last_message_time || a.updated_at).getTime();
        const bTime = new Date(b.last_message_time || b.updated_at).getTime();
        return bTime - aTime;
      })[0];

    if (mostRecentConversation) {
      void openConversation(mostRecentConversation);
      return;
    }

    setSelectedConversation(null);
    setMessages([]);
  }, [
    conversations,
    hasCustomerScope,
    isLoadingConvos,
    openConversation,
    scopedContactId,
    scopedCustomerId,
    selectedConversation,
  ]);

  // Real-time subscription for new messages
  const selectedConversationId = selectedConversation?.id;

  useEffect(() => {
    let cancelled = false;

    const loadCustomerContext = async () => {
      if (!selectedConversation) {
        setCustomerContext(null);
        setIsLoadingCustomerContext(false);
        return;
      }

      const conversation = selectedConversation;
      const customerStatus = conversation.customer_status ?? "new";
      const baseAttributes: Array<{ label: string; value: string }> = [
        { label: "Customer status", value: STAGE_META[customerStatus].label },
        { label: "Preferred channel", value: channelBadge(conversation.channel).label },
      ];
      if (conversation.customer_email) {
        baseAttributes.push({ label: "Email", value: conversation.customer_email });
      }
      if (conversation.customer_contact_phone) {
        baseAttributes.push({ label: "Phone", value: formatPhone(conversation.customer_contact_phone) });
      }

      if (conversation.is_sample) {
        const name = conversation.customer_name || "This customer";
        const channel = channelBadge(conversation.channel).label;
        if (!cancelled) {
          setCustomerContext({
            synopsis: `${name} is a ${STAGE_META[customerStatus].label.toLowerCase()} customer who most recently contacted you through ${channel}.`,
            recommendedNextStep: conversation.status === "human_takeover"
              ? "Continue the personal follow-up and resolve the customer’s latest request."
              : "Review the latest message and let the AI continue handling the conversation.",
            attributes: baseAttributes,
          });
          setIsLoadingCustomerContext(false);
        }
        return;
      }

      setIsLoadingCustomerContext(true);
      try {
        let projection: any = null;
        let customAttributes: Record<string, unknown> = {};

        if (conversation.customer_contact_id && businessId) {
          const [{ data: projectionData }, { data: contactData }] = await Promise.all([
            (supabase as any)
              .from("contact_projections")
              .select("synopsis, synopsis_llm, synopsis_llm_data, synopsis_generation_status, synopsis_schema_version, preferred_channel, total_bookings, lifetime_value")
              .eq("business_id", businessId)
              .eq("contact_id", conversation.customer_contact_id)
              .maybeSingle(),
            supabase
              .from("contacts")
              .select("custom_attributes")
              .eq("business_id", businessId)
              .eq("contact_id", conversation.customer_contact_id)
              .is("tombstoned_at", null)
              .maybeSingle(),
          ]);
          projection = projectionData;
          customAttributes = (contactData?.custom_attributes as Record<string, unknown> | null) ?? {};
        }

        const llmReady = projection?.synopsis_generation_status === "ready"
          && Number(projection?.synopsis_schema_version ?? 0) >= 3;
        const synopsis = llmReady && projection?.synopsis_llm
          ? String(projection.synopsis_llm)
          : projection?.synopsis
            ? String(projection.synopsis)
            : null;
        const rawIntel = llmReady
          && projection?.synopsis_llm_data
          && typeof projection.synopsis_llm_data === "object"
          && !Array.isArray(projection.synopsis_llm_data)
          ? projection.synopsis_llm_data as Record<string, unknown>
          : {};
        const recommendedNextStep = [rawIntel.reengagement_angle, rawIntel.predicted_next]
          .find((value): value is string => typeof value === "string" && value.trim().length > 0)
          ?.trim() ?? null;

        const attributes = [...baseAttributes];
        if (projection?.total_bookings != null) {
          attributes.push({ label: "Total bookings", value: String(projection.total_bookings) });
        }
        if (projection?.lifetime_value != null) {
          attributes.push({ label: "Lifetime value", value: `$${Number(projection.lifetime_value).toFixed(2)}` });
        }
        for (const [key, rawValue] of Object.entries(customAttributes)) {
          const value = formatCustomerAttributeValue(rawValue);
          if (!value) continue;
          attributes.push({ label: formatCustomerAttributeLabel(key), value });
          if (attributes.length >= 8) break;
        }

        if (!cancelled) {
          setCustomerContext({ synopsis, recommendedNextStep, attributes });
        }
      } catch (error) {
        console.error("Error loading customer context:", error);
        if (!cancelled) {
          setCustomerContext({ synopsis: null, recommendedNextStep: null, attributes: baseAttributes });
        }
      } finally {
        if (!cancelled) setIsLoadingCustomerContext(false);
      }
    };

    void loadCustomerContext();
    return () => {
      cancelled = true;
    };
  }, [businessId, selectedConversation]);

  useEffect(() => {
    if (!selectedConversationId || selectedConversation?.is_sample) return;

    const channel = supabase
      .channel(`messages-${selectedConversationId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "ai_messages",
          filter: `conversation_id=eq.${selectedConversationId}`,
        },
        (payload) => {
          const newMsg = payload.new as Message;
          setMessages((prev) => {
            // Prevent duplicates
            if (prev.some((m) => m.id === newMsg.id)) return prev;
            return [...prev, newMsg];
          });
        }
      )
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "ai_messages",
          filter: `conversation_id=eq.${selectedConversationId}`,
        },
        (payload) => {
          // A delivery failure reported after the send.
          const updated = payload.new as Message;
          setMessages((prev) => prev.map((m) => (m.id === updated.id ? { ...m, ...updated } : m)));
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [selectedConversation?.is_sample, selectedConversationId]);

  // Manual refresh (lightweight, no skeletons)
  const refreshConversations = useCallback(async () => {
    if (!businessId || isRefreshing) return;
    setIsRefreshing(true);
    try {
      const { data: convos, error } = await supabase
        .from("ai_conversations")
        .select("*, customers(name, email, phone, created_at, last_booking_at)")
        .eq("business_id", businessId)
        .order("updated_at", { ascending: false });

      if (error) throw error;

      const [customerStatuses, identities] = await Promise.all([
        loadCustomerStatuses(businessId, convos || []),
        loadConversationIdentities(businessId, convos || []),
      ]);
      const enriched = await enrichConversations(convos || [], customerStatuses, identities);
      if (enriched.length === 0 && showingSampleInbox) {
        setConversations(sampleInbox.conversations);
      } else {
        setConversations(enriched);
        setShowingSampleInbox(false);
      }
    } catch (e: any) {
      console.error("Error refreshing conversations:", e);
    } finally {
      setIsRefreshing(false);
    }
  }, [businessId, isRefreshing, sampleInbox.conversations, showingSampleInbox]);

  // Filter + sort conversations
  // Sort by the actual last-message time (newest first), not the DB's
  // updated_at. updated_at isn't reliably bumped on every new message, so
  // ordering on it produces a jumbled inbox; last_message_time is what we
  // display, so we sort on the same value to keep the inbox chronological.
  const contactPhotoUrls = useContactAvatarUrls(
    businessId,
    conversations.flatMap((convo) => (convo.customer_contact_id && !convo.is_sample ? [convo.customer_contact_id] : [])),
  );

  const filteredConversations = conversations
    .filter((c) => {
      if (!conversationBelongsToCustomer(c, scopedCustomerId, scopedContactId)) return false;

      const channel = (c.channel || "whatsapp").toLowerCase();
      if (!platformFilters.includes(channel as PlatformFilter)) return false;

      const customerStatus = c.customer_status ?? "new";
      if (!customerStatusFilters.includes(customerStatus)) return false;

      const attention: AttentionFilter | null = c.status === "human_takeover"
        ? "human"
        : c.status === "active"
          ? "ai"
          : null;
      if (attention && !attentionFilters.includes(attention)) return false;
      // Text search
      if (!searchQuery.trim()) return true;
      const q = searchQuery.toLowerCase();
      return (
        c.customer_name?.toLowerCase().includes(q) ||
        conversationIdentityLabel(c)?.toLowerCase().includes(q) ||
        c.customer_email?.toLowerCase().includes(q) ||
        channelBadge(c.channel).label.toLowerCase().includes(q) ||
        formatConversationIdentity(c).toLowerCase().includes(q) ||
        c.last_message?.toLowerCase().includes(q)
      );
    })
    .sort((a, b) => {
      const ta = new Date(a.last_message_time || a.updated_at).getTime();
      const tb = new Date(b.last_message_time || b.updated_at).getTime();
      return tb - ta;
    });
  const toggleValue = <T,>(current: T[], value: T) =>
    current.includes(value) ? current.filter((item) => item !== value) : [...current, value];
  const clearConversationFilters = () => {
    setPlatformFilters(PLATFORM_FILTER_OPTIONS.map(({ value }) => value));
    setCustomerStatusFilters(CUSTOMER_STATUS_FILTER_OPTIONS.map(({ value }) => value));
    setAttentionFilters(ATTENTION_FILTER_OPTIONS.map(({ value }) => value));
  };
  const filterSummary = (selected: number, total: number) => (selected === total ? "All" : `${selected} of ${total}`);
  const filterMenuItems = [
    { key: "platform" as const, label: "Platform", icon: Network, summary: filterSummary(platformFilters.length, PLATFORM_FILTER_OPTIONS.length) },
    { key: "status" as const, label: "Customer status", icon: Activity, summary: filterSummary(customerStatusFilters.length, CUSTOMER_STATUS_FILTER_OPTIONS.length) },
    { key: "attention" as const, label: "Attention", icon: User, summary: filterSummary(attentionFilters.length, ATTENTION_FILTER_OPTIONS.length) },
  ];
  const activeFilterCount = filterMenuItems.filter((item) => item.summary !== "All").length;
  const hasActiveFilters = platformFilters.length !== PLATFORM_FILTER_OPTIONS.length
    || customerStatusFilters.length !== CUSTOMER_STATUS_FILTER_OPTIONS.length
    || attentionFilters.length !== ATTENTION_FILTER_OPTIONS.length;

  // Status Updates
  const updateConversationStatus = async (status: "active" | "human_takeover" | "closed") => {
    if (!selectedConversation) return;

    if (selectedConversation.is_sample) {
      setConversations((prev) => prev.map((conversation) =>
        conversation.id === selectedConversation.id ? { ...conversation, status } : conversation,
      ));
      setSelectedConversation((prev) => prev ? { ...prev, status } : null);
      toast({
        title: "Sample status updated",
        description: `This preview conversation is now ${STATUS_CONFIG[status].label.toLowerCase()}.`,
      });
      return;
    }

    // Intercept "Resume AI" when quota is exhausted
    if (status === "active" && isQuotaExhausted && !isUnlimitedPlan) {
      setQuotaModalOpen(true);
      return;
    }

    setIsUpdatingStatus(true);
    try {
      const { error } = await supabase
        .from("ai_conversations")
        .update({ status })
        .eq("id", selectedConversation.id);

      if (error) throw error;

      // Update local state immediately
      setConversations(prev => prev.map(c => c.id === selectedConversation.id ? { ...c, status } : c));
      setSelectedConversation(prev => prev ? { ...prev, status } : null);

      toast({ title: "Status updated", description: `Conversation marked as ${STATUS_CONFIG[status].label}.` });
    } catch (e: any) {
      console.error("Error updating status:", e);
      toast({ title: "Error", description: "Failed to update conversation status.", variant: "destructive" });
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  // Send Message
  const sendMessage = async () => {
    if (!selectedConversation || !messageInput.trim() || !businessId) return;
    if (selectedConversation.is_sample) {
      const content = messageInput.trim();
      const createdAt = new Date().toISOString();
      const message: Message = {
        id: `sample-reply-${createdAt}`,
        conversation_id: selectedConversation.id,
        sender_type: "human_owner",
        content,
        created_at: createdAt,
      };
      setMessages((prev) => [...prev, message]);
      setConversations((prev) => prev.map((conversation) =>
        conversation.id === selectedConversation.id
          ? {
              ...conversation,
              last_message: content,
              last_message_time: createdAt,
              message_count: (conversation.message_count || 0) + 1,
            }
          : conversation,
      ));
      setSelectedConversation((prev) => prev
        ? {
            ...prev,
            last_message: content,
            last_message_time: createdAt,
            message_count: (prev.message_count || 0) + 1,
          }
        : null);
      setMessageInput("");
      return;
    }
    // Sample threads are local, so only real ones are held to the reply window.
    if (replyBlocked) return;
    setIsSending(true);
    try {
      const { error } = await supabase.functions.invoke('send-channel-message', {
        body: {
          business_id: businessId,
          conversation_id: selectedConversation.id,
          content: { type: 'text', text: messageInput.trim() },
          sender_type: 'human_owner'
        }
      });

      if (error) throw error;

      setMessageInput("");
      // Real-time subscription will pick up the new message
    } catch (e) {
      console.error("Error sending message:", e);
      toast({
        title: "Not sent",
        description: await readEdgeError(e, "Failed to send message."),
        variant: "destructive",
      });
      // A refusal may mean the window just closed; re-check it.
      replyWindow.refresh();
    } finally {
      setIsSending(false);
    }
  };

  // Render

  return (
    <div className="animate-in fade-in flex h-full flex-col overflow-hidden duration-500">
      <AdminTabActionBar className="!top-0 !mx-0 !mt-0 gap-2 px-4 md:px-6">
        <div className="relative w-full max-w-[320px]">
          <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="search"
            aria-label="Search conversations"
            placeholder="Search conversations..."
            className="admin-toolbar-input pl-8"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
          />
        </div>
        <Popover
          open={filterOpen}
          onOpenChange={(open) => {
            setFilterOpen(open);
            if (!open) setFilterPage("menu");
          }}
        >
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="admin-toolbar-button font-normal">
              <SlidersHorizontal className="h-3.5 w-3.5" />
              Filter
              {activeFilterCount > 0 && (
                <span className="ml-1 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[9px] font-semibold text-primary-foreground">
                  {activeFilterCount}
                </span>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[340px] overflow-hidden p-0" align="start">
            {filterPage === "menu" ? (
              <div className="py-1">
                {hasActiveFilters && (
                  <div className="flex justify-end px-3 py-1">
                    <button type="button" onClick={clearConversationFilters} className="text-xs text-primary hover:underline">
                      Clear all
                    </button>
                  </div>
                )}
                {filterMenuItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <button
                      key={item.key}
                      type="button"
                      className="flex w-full items-center gap-3 px-3 py-1.5 text-left text-sm transition-colors hover:bg-muted/60"
                      onClick={() => setFilterPage(item.key)}
                    >
                      <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
                      <span className="font-medium">{item.label}</span>
                      <span className={cn("ml-auto max-w-[120px] truncate text-xs", item.summary === "All" ? "text-muted-foreground" : "font-medium text-foreground")}>
                        {item.summary}
                      </span>
                      <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                    </button>
                  );
                })}
              </div>
            ) : (
              <div>
                <button
                  type="button"
                  className="flex w-full items-center gap-2 border-b px-3 py-2 text-sm font-medium hover:bg-muted/60"
                  onClick={() => setFilterPage("menu")}
                >
                  <ArrowLeft className="h-4 w-4" />
                  {filterMenuItems.find((item) => item.key === filterPage)?.label}
                </button>
                <div className="py-1">
                  {filterPage === "platform" && PLATFORM_FILTER_OPTIONS.map((option) => {
                    const platform = channelBadge(option.value);
                    const PlatformIcon = platform.Icon;
                    const isSelected = platformFilters.includes(option.value);
                    return (
                      <button
                        key={option.value}
                        type="button"
                        aria-pressed={isSelected}
                        onClick={() => setPlatformFilters((current) => toggleValue(current, option.value))}
                        className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm transition-colors hover:bg-muted/60"
                      >
                        <PlatformIcon className={cn("h-3.5 w-3.5", platform.iconClass)} size={14} />
                        <span>{option.label}</span>
                        <FilterCheckbox checked={isSelected} />
                      </button>
                    );
                  })}
                  {filterPage === "status" && CUSTOMER_STATUS_FILTER_OPTIONS.map((option) => {
                    const isSelected = customerStatusFilters.includes(option.value);
                    return (
                      <button
                        key={option.value}
                        type="button"
                        aria-pressed={isSelected}
                        onClick={() => setCustomerStatusFilters((current) => toggleValue(current, option.value))}
                        className="flex w-full items-center px-3 py-1.5 text-left transition-colors hover:bg-muted/60"
                      >
                        <Badge variant="outline" className={cn("h-5 px-2 py-0 text-[10px] font-medium", STAGE_META[option.value].badge)}>
                          {option.label}
                        </Badge>
                        <FilterCheckbox checked={isSelected} />
                      </button>
                    );
                  })}
                  {filterPage === "attention" && ATTENTION_FILTER_OPTIONS.map((option) => {
                    const isSelected = attentionFilters.includes(option.value);
                    return (
                      <button
                        key={option.value}
                        type="button"
                        aria-pressed={isSelected}
                        onClick={() => setAttentionFilters((current) => toggleValue(current, option.value))}
                        className="flex w-full items-center px-3 py-1.5 text-left text-sm transition-colors hover:bg-muted/60"
                      >
                        <span>{option.label}</span>
                        <FilterCheckbox checked={isSelected} />
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </PopoverContent>
        </Popover>
        <Button
          variant="outline"
          size="sm"
          className="admin-toolbar-button ml-auto"
          onClick={refreshConversations}
          disabled={isRefreshing}
          title="Refresh conversations"
          aria-label="Refresh conversations"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${isRefreshing ? "animate-spin" : ""}`} />
          Re-sync
        </Button>
      </AdminTabActionBar>

      <div className="flex min-h-0 flex-1 px-4 pb-4 pt-2 md:px-6 md:pb-6 md:pt-3">
        <Card className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border-border shadow-none">
          {showingSampleInbox && (
            <div className="shrink-0 border-b border-border bg-sky-50/70 px-4 py-2 text-xs text-sky-900" role="status">
              This sample inbox shows how messages from every connected chat channel come together. Samples are local to this preview and are never sent.
            </div>
          )}

          <div className="flex min-h-0 flex-1">

          {/* ─── LEFT: Conversation List ─── */}
            <div
              className={cn(
                "w-full border-r bg-background md:w-[340px] flex flex-col shrink-0",
                mobileShowChat && "hidden md:flex"
              )}
            >
              {/* Conversation List */}
              <ScrollArea className="min-w-0 flex-1 [&_[data-radix-scroll-area-viewport]>div]:!block [&_[data-radix-scroll-area-viewport]>div]:!w-full [&_[data-radix-scroll-area-viewport]>div]:!min-w-0">
                {isLoadingConvos ? (
                  <div className="p-3 space-y-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <div key={i} className="flex items-center gap-3 p-3">
                        <Skeleton className="h-10 w-10 rounded-full shrink-0" />
                        <div className="flex-1 space-y-2">
                          <Skeleton className="h-4 w-32" />
                          <Skeleton className="h-3 w-48" />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : filteredConversations.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
                    <div className="h-14 w-14 rounded-full bg-muted flex items-center justify-center mb-4">
                      <Inbox className="h-7 w-7 text-muted-foreground" />
                    </div>
                    <p className="text-sm font-medium text-muted-foreground mb-1">
                      {searchQuery || hasActiveFilters ? "No matching conversations" : "No conversations yet"}
                    </p>
                    <p className="text-xs text-muted-foreground/70 max-w-[220px]">
                      {searchQuery || hasActiveFilters
                        ? "Try changing your search or filters."
                        : "Conversations will appear here when customers message your AI on a connected chat channel."}
                    </p>
                  </div>
                ) : (
                  <div className="w-full min-w-0 max-w-full">
                    {filteredConversations.map((convo) => {
                      const isSelected = selectedConversation?.id === convo.id;
                      const channelInfo = channelBadge(convo.channel);
                      const ChannelIcon = channelInfo.Icon;

                      return (
                        <button
                          key={convo.id}
                          onClick={() => openConversation(convo)}
                          className={cn(
                            "flex w-full min-w-0 max-w-full items-start gap-3 overflow-hidden border-b border-border/50 px-3 py-3 text-left transition-colors hover:bg-muted/50",
                            isSelected && "bg-muted hover:bg-muted border-l-2 border-l-foreground"
                          )}
                        >
                          {/* Avatar: the contact's photo, else channel-coloured initials */}
                          <div className={cn(
                            "relative h-10 w-10 rounded-full bg-gradient-to-br flex items-center justify-center shrink-0 mt-0.5",
                            channelInfo.bg
                          )}>
                            {convo.customer_contact_id && contactPhotoUrls.get(convo.customer_contact_id) ? (
                              <img
                                src={contactPhotoUrls.get(convo.customer_contact_id)}
                                alt=""
                                className="h-full w-full rounded-full object-cover"
                                loading="lazy"
                              />
                            ) : (
                              <span className="text-xs font-semibold text-foreground/70">
                                {conversationInitials(convo)}
                              </span>
                            )}
                            <span className="absolute -bottom-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full border border-border bg-background shadow-sm">
                              <ChannelIcon className={cn("block h-3 w-3 shrink-0", channelInfo.iconClass)} size={12} />
                            </span>
                          </div>

                          {/* Content */}
                          <div className="min-w-0 flex-1 overflow-hidden">
                            <div className="mb-0.5 flex min-w-0 items-center gap-2">
                              <span className="min-w-0 flex-1 truncate text-sm font-medium" title={conversationDisplayName(convo)}>
                                {conversationDisplayName(convo)}
                              </span>
                              <span className="text-[11px] text-muted-foreground shrink-0">
                                {formatMessageDate(convo.last_message_time || convo.updated_at)}
                              </span>
                            </div>

                            <p className="line-clamp-1 max-w-full break-words text-xs leading-relaxed text-muted-foreground [overflow-wrap:anywhere]">
                              {convo.last_message || "No messages yet"}
                            </p>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </ScrollArea>
            </div>

          {/* ─── RIGHT: Chat Thread ─── */}
          {/* min-w-0 + overflow-hidden are critical: without them, an
              unbreakable string in a child (e.g. a long Stripe URL) forces
              this flex item wider than the parent, which makes the whole
              page scroll horizontally. */}
          <div
            className={cn(
              "flex-1 flex flex-col bg-muted/20 min-w-0 overflow-hidden",
              !mobileShowChat && "hidden md:flex"
            )}
          >
            {!selectedConversation ? (
              /* Empty state */
              <div className="flex-1 flex flex-col items-center justify-center text-center px-6">
                <div className="h-16 w-16 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                  <MessageSquare className="h-8 w-8 text-primary/60" />
                </div>
                <h3 className="text-lg font-semibold mb-1 font-heading">Select a conversation</h3>
                <p className="text-sm text-muted-foreground max-w-[280px]">
                  Choose a conversation from the list to view the full chat history.
                </p>
              </div>
            ) : (
              <>
                {/* Chat Header */}
                <div className="h-14 px-4 flex items-center gap-3 border-b bg-background shrink-0">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="md:hidden h-8 w-8"
                    onClick={() => setMobileShowChat(false)}
                  >
                    <ArrowLeft className="h-4 w-4" />
                  </Button>



                  {(() => {
                    const channelInfo = channelBadge(selectedConversation.channel);
                    const ChannelIcon = channelInfo.Icon;
                    return (
                      <div className={cn(
                        "relative flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br",
                        channelInfo.bg
                      )}>
                        <span className="text-[11px] font-semibold text-foreground/70">
                          {conversationInitials(selectedConversation)}
                        </span>
                        <span className="absolute -bottom-1 -right-1 flex h-[18px] w-[18px] items-center justify-center rounded-full border border-border bg-background shadow-sm">
                          <ChannelIcon className={cn("h-2.5 w-2.5", channelInfo.iconClass)} size={10} />
                        </span>
                      </div>
                    );
                  })()}

                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">
                      {conversationDisplayName(selectedConversation)}
                    </p>
                    <p className="text-[11px] text-muted-foreground flex items-center gap-1.5 mt-0.5">
                      <span>{channelBadge(selectedConversation.channel).label}</span>
                      <span aria-hidden="true">·</span>
                      {(() => {
                        const lastActivity = new Date(selectedConversation.last_message_time || selectedConversation.updated_at);
                        const isLive = (new Date().getTime() - lastActivity.getTime()) < 5 * 60 * 1000;
                        if (isLive) {
                          return (
                            <>
                              <span className="relative flex h-2 w-2">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                              </span>
                              <span className="text-emerald-600 font-medium dark:text-emerald-400">Active now</span>
                            </>
                          );
                        }
                        return `Last active ${formatDistanceToNow(lastActivity, { addSuffix: true })}`;
                      })()}
                    </p>
                  </div>

                  {(() => {
                    const isHumanHandling = selectedConversation.status === "human_takeover";
                    return (
                      <button
                        type="button"
                        role="switch"
                        aria-checked={isHumanHandling}
                        aria-label={isHumanHandling
                          ? "Human is handling — switch to AI"
                          : "AI is handling — switch to Human"}
                        onClick={() => updateConversationStatus(isHumanHandling ? "active" : "human_takeover")}
                        disabled={isUpdatingStatus}
                        className="inline-flex items-center gap-2.5 disabled:opacity-60"
                      >
                        <span
                          className={cn(
                            "text-xs font-semibold transition-colors",
                            !isHumanHandling ? "text-foreground" : "text-muted-foreground/50",
                          )}
                        >
                          AI
                        </span>
                        <span
                          className={cn(
                            "relative inline-flex h-8 w-14 shrink-0 items-center rounded-full transition-colors",
                            isHumanHandling ? "bg-primary" : "bg-muted",
                          )}
                        >
                          <span
                            className={cn(
                              "absolute flex h-6 w-6 items-center justify-center rounded-full bg-white shadow-md transition-all duration-200",
                              isHumanHandling ? "translate-x-[28px]" : "translate-x-1",
                            )}
                          >
                            {isHumanHandling ? (
                              <User className="h-3.5 w-3.5 text-primary" />
                            ) : (
                              <Sparkles className="h-3.5 w-3.5 text-muted-foreground" />
                            )}
                          </span>
                        </span>
                        <span
                          className={cn(
                            "text-xs font-semibold transition-colors",
                            isHumanHandling ? "text-foreground" : "text-muted-foreground/50",
                          )}
                        >
                          Human
                        </span>
                      </button>
                    );
                  })()}
                </div>

                {/* Messages Area */}
                <ScrollArea className="flex-1 px-5 md:px-6">
                  {isLoadingMessages ? (
                    <div className="flex items-center justify-center py-16">
                      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    </div>
                  ) : messages.length === 0 ? (
                    <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
                      No messages in this conversation.
                    </div>
                  ) : (
                    <div className="pt-5 pb-6">
                      {groupMessagesByDate(messages).map((group) => (
                        <div key={group.label}>
                          {/* Date separator */}
                          <div className="my-5 flex items-center justify-center">
                            <span className="rounded-full bg-muted px-3 py-1 text-[10px] font-medium text-muted-foreground">
                              {group.label}
                            </span>
                          </div>

                          {/* Messages */}
                          {group.messages.map((msg) => {
                            const isCustomer = msg.sender_type === "customer";
                            const isAI = msg.sender_type === "ai";
                            const customerChannel = channelBadge(selectedConversation.channel);
                            const CustomerChannelIcon = customerChannel.Icon;
                            const senderLabel = isCustomer
                              ? selectedConversation.customer_name?.trim() || conversationIdentityLabel(selectedConversation) || "Customer"
                              : isAI
                                ? "AI"
                                : "Human";

                            return (
                              <div
                                key={msg.id}
                                className={cn(
                                  "mb-4 flex items-start gap-2.5 last:mb-0",
                                  isCustomer ? "justify-start" : "justify-end"
                                )}
                              >
                                {/* Customer profile (left side) */}
                                {isCustomer && (
                                  <div className={cn(
                                    "relative flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br",
                                    customerChannel.bg,
                                  )}>
                                    <span className="text-[10px] font-semibold text-foreground/70">
                                      {conversationInitials(selectedConversation)}
                                    </span>
                                    <span className="absolute -bottom-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full border border-border bg-background">
                                      <CustomerChannelIcon
                                        className={cn("h-2.5 w-2.5", customerChannel.iconClass)}
                                        size={10}
                                      />
                                    </span>
                                  </div>
                                )}

                                <div
                                  className={cn(
                                    "flex max-w-[72%] min-w-0 flex-col",
                                    isCustomer ? "items-start" : "items-end",
                                  )}
                                >
                                  <div className={cn(
                                    "mb-1 flex items-center gap-2 px-0.5 text-[10px]",
                                    !isCustomer && "justify-end",
                                  )}>
                                    <span className={cn(
                                      "font-semibold",
                                      isAI ? "text-primary" : "text-foreground",
                                    )}>
                                      {senderLabel}
                                    </span>
                                    <span className="text-muted-foreground">
                                      {format(new Date(msg.created_at), "h:mm a")}
                                    </span>
                                  </div>

                                  <div
                                    className={cn(
                                      "w-fit max-w-full rounded-2xl px-4 py-2.5 text-sm leading-relaxed",
                                      isCustomer
                                        ? "bg-muted text-foreground"
                                        : isAI
                                          ? "border border-primary/15 bg-primary/10 text-foreground"
                                          : "bg-muted text-foreground",
                                    )}
                                  >
                                    <MessageContent content={msg.content} />
                                  </div>

                                  {!isCustomer && msg.delivery_failed_at && (
                                    <DeliveryFailedNote
                                      error={msg.delivery_error}
                                      className="px-0.5 text-destructive"
                                    />
                                  )}
                                </div>

                                {/* AI/Human profile (right side) */}
                                {!isCustomer && (
                                  <div className={cn(
                                    "flex h-8 w-8 shrink-0 items-center justify-center rounded-full",
                                    isAI
                                      ? "bg-primary/10"
                                      : "bg-muted"
                                  )}>
                                    {isAI ? (
                                      <Sparkles className="h-3.5 w-3.5 text-primary" />
                                    ) : (
                                      <User className="h-3.5 w-3.5 text-muted-foreground" />
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      ))}
                      <div ref={messagesEndRef} />
                    </div>
                  )}
                </ScrollArea>

                {/* Footer info bar or input area */}
                {selectedConversation.status === "human_takeover" ? (
                  (() => {
                    const isTikTok =
                      (selectedConversation.channel ?? "").toLowerCase() === "tiktok";
                    const lastCustomerMsg = [...messages]
                      .reverse()
                      .find((m) => m.sender_type === "customer");
                    const isTikTokWindowExpired =
                      isTikTok &&
                      (!lastCustomerMsg ||
                        Date.now() - new Date(lastCustomerMsg.created_at).getTime() >
                          48 * 3_600_000);

                    return (
                      <div className="p-3 border-t bg-background shrink-0">
                        {isTikTokWindowExpired ? (
                          <div className="flex items-center justify-center gap-2 rounded-md border border-amber-200 bg-amber-50/70 px-3 py-2 text-xs text-amber-900">
                            <Clock className="h-3.5 w-3.5 shrink-0" />
                            <span>
                              TikTok 48-hour messaging window expired. Outbound replies are paused until the customer messages again.
                            </span>
                          </div>
                        ) : (
                          <>
                            {replyBlocked && (
                              <div className="mb-2">
                                <ReplyWindowNotice
                                  channelLabel={replyWindow.channelLabel}
                                  lastCustomerAt={replyWindow.lastCustomerAt}
                                >
                                  {businessId && (selectedConversation.channel ?? "whatsapp") === "whatsapp" && (
                                    <ReplyTemplatePicker businessId={businessId} conversationId={selectedConversation.id} />
                                  )}
                                </ReplyWindowNotice>
                              </div>
                            )}
                            <div className="flex gap-2">
                              <Input
                                placeholder={replyBlocked ? "Waiting for them to message you" : "Type a message to the customer..."}
                                className="flex-1"
                                value={messageInput}
                                onChange={(e) => setMessageInput(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault();
                                    sendMessage();
                                  }
                                }}
                                disabled={isSending || replyBlocked}
                              />
                              <Button onClick={sendMessage} disabled={!messageInput.trim() || isSending || replyBlocked} size="icon">
                                {isSending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                              </Button>
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })()
                ) : null}
              </>
            )}
          </div>

          {selectedConversation && (
            <aside
              className="hidden w-[288px] shrink-0 flex-col border-l border-border bg-background xl:flex"
              aria-label="Customer context"
            >
              <div className="flex shrink-0 items-center px-4 pt-4">
                <h2 className="min-w-0 text-sm font-semibold text-foreground">Customer context</h2>
              </div>

              <ScrollArea className="min-h-0 flex-1">
                <div className="px-4">
                  <div className="pt-1.5">
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 text-[11px] font-medium text-primary hover:underline disabled:cursor-not-allowed disabled:text-muted-foreground disabled:no-underline"
                      disabled={!selectedConversation.customer_id && !selectedConversation.customer_contact_id}
                      onClick={() => {
                        const next = new URLSearchParams();
                        next.set("tab", "customer-details");
                        if (selectedConversation.customer_id) next.set("id", selectedConversation.customer_id);
                        if (selectedConversation.customer_contact_id) next.set("graph", selectedConversation.customer_contact_id);
                        setSearchParams(next);
                      }}
                    >
                      View full profile
                      <ArrowUpRight className="h-3 w-3" />
                    </button>
                  </div>

                  {isLoadingCustomerContext ? (
                    <div className="space-y-5 py-4">
                      <div className="space-y-2">
                        <Skeleton className="h-3 w-20" />
                        <Skeleton className="h-3 w-full" />
                        <Skeleton className="h-3 w-5/6" />
                      </div>
                      <div className="space-y-2">
                        <Skeleton className="h-3 w-32" />
                        <Skeleton className="h-3 w-full" />
                      </div>
                      <div className="space-y-2">
                        <Skeleton className="h-3 w-20" />
                        <Skeleton className="h-8 w-full" />
                        <Skeleton className="h-8 w-full" />
                      </div>
                    </div>
                  ) : (
                    <div className="divide-y divide-border">
                      <section className="py-4">
                        <div className="mb-1.5 flex items-center gap-1.5">
                          <h3 className="text-xs font-semibold text-foreground">Synopsis</h3>
                          <Sparkles className="h-3.5 w-3.5 text-primary" aria-label="AI generated" />
                        </div>
                        <p className="text-xs leading-relaxed text-muted-foreground">
                          {customerContext?.synopsis || "No synopsis available yet."}
                        </p>
                      </section>

                      <section className="py-4">
                        <div className="mb-1.5 flex items-center gap-1.5">
                          <h3 className="text-xs font-semibold text-foreground">Recommended next step</h3>
                          <Sparkles className="h-3.5 w-3.5 text-primary" aria-label="AI generated" />
                        </div>
                        <p className="text-xs leading-relaxed text-muted-foreground">
                          {customerContext?.recommendedNextStep || "No recommended next step yet."}
                        </p>
                      </section>

                      <section className="py-4">
                        <h3 className="mb-2 text-xs font-semibold text-foreground">Attributes</h3>
                        <dl className="space-y-2.5">
                          {(customerContext?.attributes ?? []).map((attribute) => (
                            <div key={attribute.label} className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)] gap-3">
                              <dt className="text-[11px] text-muted-foreground">{attribute.label}</dt>
                              <dd className="truncate text-right text-[11px] font-medium text-foreground" title={attribute.value}>
                                {attribute.value}
                              </dd>
                            </div>
                          ))}
                        </dl>
                      </section>
                    </div>
                  )}
                </div>
              </ScrollArea>
            </aside>
          )}
          </div>
        </Card>
      </div>

      {/* ── AI Quota Exhaustion Modal ── */}
      <Dialog open={quotaModalOpen} onOpenChange={setQuotaModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="mx-auto mb-3 h-14 w-14 rounded-full bg-amber-100 dark:bg-amber-950/50 flex items-center justify-center">
              <Zap className="h-7 w-7 text-amber-600 dark:text-amber-400" />
            </div>
            <DialogTitle className="text-center text-lg">Monthly engaged customer limit reached</DialogTitle>
            <DialogDescription className="text-center text-sm leading-relaxed pt-2">
              {macQuota.macUsed} of {macQuota.effectiveLimit ?? 0} monthly engaged customers have messaged your AI this cycle. Until your cycle resets, new customers will need to be{" "}
              <strong>replied to manually</strong> from this dashboard.
            </DialogDescription>
          </DialogHeader>

          <div className="rounded-lg border bg-muted/40 p-4 space-y-2 text-sm">
            <div className="flex items-center gap-2 text-muted-foreground">
              <Clock className="h-4 w-4 shrink-0" />
              <span>
                Resets on{" "}
                <strong className="text-foreground">
                  {quotaResetDate || "your next billing cycle"}
                </strong>
              </span>
            </div>
            <p className="text-muted-foreground text-xs leading-relaxed pl-6">
              {(() => {
                const limits = plans[macQuota.planKey ?? ""]?.limits;
                const hasOverage = (limits?.mac_overage_pack_size ?? null) !== null;
                const atCeiling =
                  macQuota.macCeiling !== null && macQuota.effectiveLimit !== null &&
                  macQuota.effectiveLimit >= macQuota.macCeiling;
                return hasOverage && !atCeiling
                  ? "Upgrade your plan for a higher engaged-customer allowance, or purchase overage to keep your AI online for the rest of this cycle."
                  : "Upgrade your plan for a higher engaged-customer allowance to keep your AI online for the rest of this cycle.";
              })()}
            </p>
          </div>

          <DialogFooter className="flex-col gap-2 sm:flex-col">
            {(() => {
              const limits = plans[macQuota.planKey ?? ""]?.limits;
              const packSize = limits?.mac_overage_pack_size ?? null;
              const packPrice = limits?.mac_overage_pack_price_usd ?? null;
              // No overage on this plan (Starter), or already maxed to the
              // ceiling → hide the buy button (upgrade is the only path).
              const atCeiling =
                macQuota.macCeiling !== null && macQuota.effectiveLimit !== null &&
                macQuota.effectiveLimit >= macQuota.macCeiling;
              if (packSize === null || packPrice === null || atCeiling) return null;
              return (
                <Button
                  className="w-full gap-2"
                  disabled={overageLoading}
                  onClick={async () => {
                    const res = await purchaseOverage(1);
                    if (res?.success) setQuotaModalOpen(false);
                  }}
                >
                  {overageLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />}
                  Buy {packSize} more engaged customers (S${packPrice})
                </Button>
              );
            })()}
            <Button
              variant="outline"
              className="w-full gap-2"
              onClick={() => {
                setQuotaModalOpen(false);
                setSearchParams({ tab: "settings", settings: "billing" }, { replace: true });
              }}
            >
              <Crown className="h-4 w-4" />
              Upgrade plan
            </Button>
            <Button
              variant="ghost"
              className="w-full text-muted-foreground"
              onClick={() => setQuotaModalOpen(false)}
            >
              Dismiss
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Conversations;
